import { Box, Divider, Drawer, Tab, Tabs, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { msspDashboardKeys } from "../../../../api/queryKeys";
import CustomToolTip from "../../../../components/CustomTooltip";
import LabelComponentProp from "../../../../components/LabelComponentProp";
import { ControlSeverityMssp } from "../../../../utils/constants";
import IntegrationImage from "../../../stack/integrations/components/IntegrationImage";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";
import CoverageScoreLabel from "./CoverageScoreLabel";
import StackCoverageTable from "./StackCoverageTable";
import {
  calculateCoverageScore,
  calculateTotalControls,
  calculateTotalGaps,
  filterTenantsBySeverity,
  generateSeverityTabs,
} from "./utils";

const ToolSeveritiesDrawer = ({ selectedTool, open, onClose }) => {
  const colors = useColors();
  const { getMSSPStackCoverageTable, trackEvent } = useAPI();
  const [activeSeverity, setActiveSeverity] = useState(
    ControlSeverityMssp.HIGH
  );

  const { data: tenantsDataTable, isLoading: isLoadingTenantsDataTable } =
    useQuery({
      queryKey: msspDashboardKeys.stackCoverageTable(selectedTool?.name),
      queryFn: () => getMSSPStackCoverageTable(selectedTool?.name),
      select: (data) => data.data,
      enabled: !!selectedTool?.name,
    });

  const totalGaps = useMemo(
    () => calculateTotalGaps(selectedTool),
    [selectedTool]
  );

  const totalControls = useMemo(
    () => calculateTotalControls(selectedTool, totalGaps),
    [selectedTool, totalGaps]
  );

  const totalCoverageScore = useMemo(
    () => calculateCoverageScore(selectedTool, totalGaps, totalControls),
    [selectedTool, totalGaps, totalControls]
  );

  const SEVERITY_TABS = useMemo(
    () => generateSeverityTabs(selectedTool, colors),
    [selectedTool, colors]
  );

  const mapTenantToRow = useMemo(
    () =>
      filterTenantsBySeverity(tenantsDataTable, activeSeverity, selectedTool),
    [tenantsDataTable, activeSeverity, selectedTool]
  );

  const tabStyle = {
    "&.Mui-selected": {
      boxShadow:
        "-3px 0.5px 5px 0.5px rgba(203, 203, 203, 0.40), 3px 0.5px 5px 0.5px rgba(203, 203, 203, 0.40)",
    },
    borderRadius: "8px 8px 0 0",
    textTransform: "none",
    minHeight: "48px",
  };

  // for Tooltip in disabled tab
  const TabSeverity = ({ tab }) => {
    return (
      <Tab
        key={tab.value}
        value={tab.value}
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: tab.color,
              }}
            />
            <Typography variant="body2">
              {tab.label +
                (tab.numberOfControls > 0
                  ? " (" + tab.numberOfControls + ")"
                  : "")}
            </Typography>
          </Box>
        }
        sx={tabStyle}
        disabled={tab.disabled}
      />
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        setActiveSeverity(ControlSeverityMssp.HIGH);
        onClose();
      }}
      PaperProps={{
        style: {
          width: "850px",
          backgroundColor: colors.grey_50,
          borderRadius: "8px 0 0 0",
          overflowY: "auto",
          padding: 32,
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {" "}
          <Typography variant="h5">
            {
              IntegrationsMapping[selectedTool?.name?.toLowerCase()]
                ?.displayName
            }{" "}
            Controls Coverage Across All Tenants
          </Typography>
          {/* Summary Row */}
          <Box sx={{ display: "flex", flexDirection: "row", gap: 3 }}>
            <LabelComponentProp
              label={"Tool"}
              textTypography="caption"
              sx={{
                display: "flex",
                alignItems: "left",
              }}
            >
              <IntegrationImage
                name={selectedTool?.name?.toLowerCase()}
                width={24}
                newLogo={true}
              />
            </LabelComponentProp>
            <Divider orientation="vertical" flexItem />
            <LabelComponentProp
              label={"Coverage Score"}
              textTypography="caption"
              sx={{
                display: "flex",
                alignItems: "left",
              }}
            >
              <CoverageScoreLabel value={totalCoverageScore} />
            </LabelComponentProp>{" "}
            <Divider orientation="vertical" flexItem />
            <LabelComponentProp
              label={"Total Controls"}
              textTypography="caption"
              sx={{
                display: "flex",
                alignItems: "left",
              }}
            >
              <Typography variant="caption" sx={{ color: colors.text_primary }}>
                {totalControls}
              </Typography>
            </LabelComponentProp>
          </Box>
          <Divider
            orientation="horizontal"
            flexItem
            sx={{
              boxShadow: "0px 1px 6.3px 0px " + colors.grey_24percent,
              color: colors.divider,
            }}
          />
        </Box>
        <Box
          sx={{
            boxShadow: "0px 0px 2px 1px rgba(203, 203, 203, 0.50)",
            borderRadius: "8px",
          }}
        >
          {/* Severity Tabs */}
          <Box sx={{}}>
            <Tabs
              value={activeSeverity}
              onChange={(_, v) => {
                setActiveSeverity(v);
                trackEvent(
                  "User clicked on Severity Tab in Stack Utilization and Coverage Drawer"
                );
              }}
              sx={{
                paddingBottom: "4px",
                ".MuiTabs-indicator": {
                  display: "none",
                  border: "1px solid " + colors.wizard_box_shadow,
                },
              }}
            >
              {SEVERITY_TABS.map((tab) =>
                tab.disabled ? (
                  <CustomToolTip
                    key={tab.value}
                    title={"The tool is deployed on all tenants"}
                  >
                    <Box>
                      <TabSeverity tab={tab} />
                    </Box>
                  </CustomToolTip>
                ) : (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: tab.color,
                          }}
                        />
                        <Typography variant="body2">
                          {tab.label +
                            (tab.numberOfControls > 0
                              ? " (" + tab.numberOfControls + ")"
                              : "")}
                        </Typography>
                      </Box>
                    }
                    sx={tabStyle}
                    disabled={tab.disabled}
                  />
                )
              )}
            </Tabs>
          </Box>
          {/* Table */}
          <StackCoverageTable
            rows={mapTenantToRow}
            activeSeverity={activeSeverity}
            isLoadingTenantsData={isLoadingTenantsDataTable}
          />
        </Box>
      </Box>
    </Drawer>
  );
};

export default ToolSeveritiesDrawer;
